/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { StorageAccountItem } from "./types";
import {
  StorageAccount,
  StorageSettings,
  StorageProvider,
} from "@dashboard-v3/api";
import { useFetch } from "utils/api/useFetch";
import { translationKey } from "components/StorageProviderLabel";

type Config = {
  includeOrgAccounts?: boolean;
  excludedProviders?: Array<StorageProvider>;
};

export default function useStorageAccounts(config: Config) {
  const { t } = useTranslation("components");
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: accounts,
    isLoading: accountsLoading,
    mutate: refetchAccounts,
    error: accountsError,
  } = useFetch<StorageAccount[]>("/storage-accounts");

  // Settings only matter to build the ORGANIZATION options
  const settingsUrl = config.includeOrgAccounts
    ? "/storages/settings"
    : undefined;

  const {
    data: storageSettings,
    isLoading: settingsLoading,
    mutate: refetchSettings,
    error: settingsError,
  } = useFetch<StorageSettings>(settingsUrl);

  const isLoading = settingsLoading || accountsLoading;
  const items = isLoading
    ? null
    : mapToItems(config, storageSettings, accounts);

  async function reFetchOptions() {
    refetchAccounts();
    refetchSettings();
  }

  useEffect(() => {
    if (accountsError || settingsError) {
      enqueueSnackbar(t("storageAccounts.errors.fetchError"), {
        variant: "error",
      });
    }
  }, [accountsError, settingsError]);

  return {
    items,
    reFetchOptions,
    isLoading: accountsLoading || settingsLoading,
  };
}

function mapToItems(
  config: Config,
  settings?: StorageSettings,
  storageAccounts?: StorageAccount[]
) {
  const { includeOrgAccounts, excludedProviders = [] } = config;
  const individualItems = mpaIndividualItems(storageAccounts);
  const byProvider = (item: StorageAccountItem) => {
    return !excludedProviders.includes(item.storageAccount.provider);
  };

  if (!includeOrgAccounts) {
    return addIndividualsSeparator(individualItems.filter(byProvider));
  }

  const orgItems = mapOrgAccounts(settings);
  const allItems = orgItems.concat(individualItems);

  return addIndividualsSeparator(allItems.filter(byProvider));
}

function addIndividualsSeparator(items: StorageAccountItem[]) {
  const firstItem = items.find(
    item => item.storageAccount.authentication === "INDIVIDUAL"
  );

  if (firstItem) firstItem.option.showSeparator = true;

  return items;
}

function mapOrgAccounts(settings?: StorageSettings): Array<StorageAccountItem> {
  if (!settings) return [];

  return [
    itemOfProvider("BOX", Boolean(settings.box?.enterpriseId)),
    itemOfProvider("GOOGLEDRIVE", Boolean(settings.gapps?.installed)),
    itemOfProvider("ONEDRIVE", Boolean(settings.onedrive?.installed)),
    itemOfProvider("MXHERO_STORAGE", Boolean(settings.mxheroStorage?.enabled)),
  ];
}

function mpaIndividualItems(storageAccounts?: StorageAccount[]) {
  if (!storageAccounts) return [];

  return storageAccounts.map<StorageAccountItem>(item => {
    const needsReAuth = !item.synced || Boolean(item.missingRefreshToken);

    return {
      key: item.id,
      option: {
        selectable: !needsReAuth,
        needsReAuth,
        missingRefreshToken: item.missingRefreshToken,
        subTitle: translationKey(item.type),
        title: item.type === "DROPBOX" ? item.data.name : item.user,
        showSeparator: false,
      },
      storageAccount: {
        id: item.id,
        authentication: "INDIVIDUAL",
        provider: item.type,
        userId: item.user,
      },
    };
  });
}

function itemOfProvider(
  provider: StorageProvider,
  installed: boolean
): StorageAccountItem {
  return {
    key: provider,
    option: {
      selectable: installed,
      needsReAuth: !installed,
      title: `storageAccounts.option.org.${provider}`,
    },
    storageAccount: {
      authentication: "ORGANIZATION",
      provider,
    },
  };
}
